import React from "react";
import { render, screen, within } from "@testing-library/react";
import userEvent from "@testing-library/user-event";
import "@testing-library/jest-dom";

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));

jest.mock("react-router-dom", () => ({
  useParams: () => ({ config_id: "config-1", result_id: "result-1" }),
  useNavigate: () => jest.fn(),
}));

const mockFetchResult = jest.fn();
jest.mock("../hooks", () => ({
  useDigestResultGet: () => mockFetchResult,
}));

jest.mock("../components/BKGAgent", () => ({
  FloatingChatWidget: () => <div data-testid="floating-chat-widget" />,
}));

const DigestView = require("./DigestView").default;

const baseResult = {
  id: "result-1",
  workspace_id: "workspace-1",
  digest_config_id: "config-1",
  generated_at: "2026-10-12T09:00:00Z",
  generated_by: "scheduled",
  event_count: 2,
  llm_model: "test-model",
  llm_prompt_tokens: 1,
  llm_completion_tokens: 1,
  generation_duration_seconds: 1.5,
  status: "completed",
  error_message: null,
};

const item = (overrides: Record<string, unknown>) => ({
  rank: 1,
  catalyst_event_id: "event-1",
  company_name: "Example Bio",
  title: "Example Bio raises $150M Series B",
  event_type: "Funding Round",
  event_date: "2026-10-08",
  summary: "Summary",
  rationale: "Rationale",
  source_url: null,
  reported_date: null,
  ...overrides,
});

describe("DigestView executive snapshot", () => {
  beforeEach(() => mockFetchResult.mockReset());

  it("shows the snapshot, per-item signals and the sentiment table", async () => {
    mockFetchResult.mockResolvedValue({
      ...baseResult,
      executive_summary: [
        "Example Bio raised $150M.",
        "Other Tx missed Phase 3.",
      ],
      items: [
        item({ sentiment: "positive" }),
        item({
          rank: 2,
          company_name: "Other Tx",
          title: "Other Tx misses Phase 3 endpoint",
          sentiment: "negative",
        }),
        item({
          rank: 3,
          company_name: "Third Co",
          title: "No signal",
          sentiment: null,
        }),
      ],
    });

    render(<DigestView />);

    expect(await screen.findByText("Executive snapshot")).toBeInTheDocument();
    expect(screen.getByText("Example Bio raised $150M.")).toBeInTheDocument();
    expect(screen.getByText("Other Tx missed Phase 3.")).toBeInTheDocument();

    const table = screen.getByRole("table");
    const rows = within(table).getAllByRole("row");
    expect(rows).toHaveLength(3); // header + the two items that carry a signal
    expect(within(rows[1]).getByText("Example Bio")).toBeInTheDocument();
    expect(within(rows[1]).getByText("Positive")).toBeInTheDocument();
    expect(within(rows[2]).getByText("Other Tx")).toBeInTheDocument();
    expect(within(rows[2]).getByText("Negative")).toBeInTheDocument();
    expect(within(table).queryByText("Third Co")).toBeNull();
    expect(
      screen.getByText(/not investment recommendations/),
    ).toBeInTheDocument();
  });

  it("renders a digest generated before the snapshot existed as before", async () => {
    mockFetchResult.mockResolvedValue({ ...baseResult, items: [item({})] });

    render(<DigestView />);

    expect(await screen.findByText("Example Bio")).toBeInTheDocument();
    expect(screen.queryByText("Executive snapshot")).toBeNull();
    expect(screen.queryByText("Company sentiment summary")).toBeNull();
    expect(screen.queryByRole("table")).toBeNull();
  });

  it("lists items alphabetically by default and by rank on request", async () => {
    mockFetchResult.mockResolvedValue({
      ...baseResult,
      items: [
        item({ rank: 1, company_name: "Zeta Bio", title: "Zeta headline" }),
        item({
          rank: 2,
          company_name: "alpha Pharma",
          title: "Alpha headline",
        }),
        item({ rank: 3, company_name: null, title: "Unnamed headline" }),
        item({ rank: 4, company_name: "Beta Tx", title: "Beta headline" }),
      ],
    });

    render(<DigestView />);
    await screen.findByText("Zeta headline");

    const headlines = () =>
      screen.getAllByText(/ headline$/).map((node) => node.textContent);

    expect(headlines()).toEqual([
      "Alpha headline",
      "Beta headline",
      "Zeta headline",
      "Unnamed headline",
    ]);

    await userEvent.click(screen.getByRole("button", { name: "Rank" }));
    expect(headlines()).toEqual([
      "Zeta headline",
      "Alpha headline",
      "Unnamed headline",
      "Beta headline",
    ]);

    await userEvent.click(screen.getByRole("button", { name: "Company A–Z" }));
    expect(headlines()[0]).toBe("Alpha headline");
  });
});
